import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/client';
import type { BudgetData, Category, LineItem, Settings } from '../types';
import type { CategoryGroup, DerivedBudget } from '../lib/totals';
import { deriveBudget } from '../lib/totals';
import { downloadBudgetCsv } from '../lib/csvExport';
import { downloadBudgetXlsx } from '../lib/xlsxExport';
import { useDragSystem } from '../lib/dragSystem';
import type { DragKind, MoveTarget } from '../lib/dragSystem';
import { ViewerContext } from '../lib/viewerContext';
import { Masthead } from '../components/Masthead';
import { ControlPanel } from '../components/ControlPanel';
import { SummaryStrip } from '../components/SummaryStrip';
import { BudgetTable } from '../components/BudgetTable';
import { ProjectsSection } from '../components/ProjectsSection';
import { GrandTotalPanel } from '../components/GrandTotalPanel';
import { HistoricalStrip } from '../components/HistoricalStrip';
import { ArchivePanel } from '../components/ArchivePanel';
import { AdminPanel } from '../components/AdminPanel';
import { QuotaPolicyPanel } from '../components/QuotaPolicyPanel';
import { computeDiocesanFyTotals, type QuotaBinding } from '../lib/quotaBinding';
import { SectionsPage } from '../components/SectionsPage';
import { CashflowPage } from '../components/CashflowPage';

type LoadResult = { data: BudgetData; source: 'saved' | 'seed' };
type XeroAccountForBudget = { code: string; name: string; kind: 'income' | 'expense' };
type XeroPLActuals = {
  periods: string[];
  byCode: Record<string, Record<string, number>>;
  nameByCode: Record<string, string>;
};

const newId = (prefix: string): string =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

const HISTORY_CAP = 50;

// GL 210 — Diocesan Quota. The fixed budget line the bound quota policy's
// schedule total flows into (quotaY* slots).
const QUOTA_GL_CODE = '210';

type Busy = 'load' | 'save' | 'refresh' | null;
type Kind = 'income' | 'expense';

const arrayKey = (kind: Kind): 'income' | 'expenses' => (kind === 'income' ? 'income' : 'expenses');

// Counts visible (non-hidden, non-tombstoned) rows so subsequent sections
// can compute their starting line numbers. Hidden rows are filtered the
// same way as in BudgetTable / ProjectBlock for consistency.
function sumActuals(rows: { code: string }[], byCode: Map<string, number>): number {
  return rows.reduce((s, r) => s + (byCode.get(r.code) ?? 0), 0);
}

function countVisibleRows(
  derived: DerivedBudget,
  settings: Settings,
  showHiddenIn: Set<string>,
): number {
  const visible = (g: CategoryGroup) =>
    [...g.incomeItems, ...g.expenseItems].filter(
      (i) =>
        settings.showHidden || showHiddenIn.has(g.category.id) || !settings.hidden.includes(i.id),
    ).length;
  const income = derived.incomeByCategory.reduce((a, g) => a + visible(g), 0);
  const expenses = derived.expensesByCategory.reduce((a, g) => a + visible(g), 0);
  const finIncome = derived.financialIncomeByCategory.reduce((a, g) => a + visible(g), 0);
  const finExpenses = derived.financialExpensesByCategory.reduce((a, g) => a + visible(g), 0);
  return income + expenses + finIncome + finExpenses;
}

export function BudgetPage() {
  const [pristine, setPristine] = useState<BudgetData | null>(null);
  const [working, setWorking] = useState<BudgetData | null>(null);
  const [history, setHistory] = useState<BudgetData[]>([]);
  const [source, setSource] = useState<'seed' | 'saved'>('seed');
  const [busy, setBusy] = useState<Busy>('load');
  const [error, setError] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<string>('');

  // Per-section UI state — not persisted. Both let the user reveal
  // rows scoped to a single section. `showHiddenIn` reveals zero-value
  // rows the user has hidden; `showArchivedIn` reveals soft-deleted
  // (tombstoned) rows. Both keys can be category IDs (operating tables)
  // or project IDs (project blocks); the IDs don't collide because of
  // the `ic_`/`ec_`/`p_` prefixes.
  const [showHiddenIn, setShowHiddenIn] = useState<Set<string>>(() => new Set());
  const [showArchivedIn, setShowArchivedIn] = useState<Set<string>>(() => new Set());

  // The hub allowlist already gated entry to this page, so anyone who
  // reaches the React app is a registrar by definition. The legacy
  // viewer-link role is gone — keep `isViewer` as a constant so the
  // existing JSX (e.g. ViewerContext.Provider) continues to type-check.
  const [adminOpen, setAdminOpen] = useState(false);
  // Quota is a drawer over the budget (AdminPanel pattern) — the budget
  // stays visible and row 210 live-updates as bindings land.
  const [quotaOpen, setQuotaOpen] = useState(false);
  const [view, setView] = useState<'budget' | 'sections' | 'cashflow'>('budget');
  const isRegistrar = true;
  const isViewer = false;

  const quotaSaveTimer = useRef<number | null>(null);
  const inFlightSave = useRef<boolean>(false);

  const [plActuals, setPLActuals] = useState<XeroPLActuals>({
    periods: [],
    byCode: {},
    nameByCode: {},
  });
  const refreshActuals = useCallback(() => {
    api
      .budgetPLActuals()
      .then(setPLActuals)
      .catch(() => {
        // Best-effort — actuals are decorative; don't block the budget UI.
        setPLActuals({ periods: [], byCode: {}, nameByCode: {} });
      });
  }, []);

  // Cached Xero Chart of Accounts. The "Add row" picker filters this
  // list so users can only create rows for codes Xero actually knows
  // about — no free-typed, unverified GL codes.
  const [xeroAccounts, setXeroAccounts] = useState<XeroAccountForBudget[]>([]);
  const refreshXeroAccounts = useCallback(() => {
    api
      .budgetGLAccounts()
      .then(setXeroAccounts)
      .catch(() => setXeroAccounts([]));
  }, []);

  // The Quota-bound row's forecast values are owned by the bound quota
  // policy run. We persist them in dedicated `quotaY*` fields rather than
  // the generic overrideY* slots so:
  //   - Y1 (locked) reads from quotaY1 cleanly
  //   - Y2/Y3 (editable) can revert to the run's number when the user
  //     clears their manual override, instead of falling through to the
  //     CPI cascade.
  //
  // Earlier builds (the retired legacy calculator) wrote output straight
  // into overrideY*. This migration sweeps any leftover values across to
  // quotaY* and clears the overrides — idempotent once data is clean.
  const reconcileBoundForecast = (data: BudgetData): BudgetData => {
    const boundCode = QUOTA_GL_CODE;
    let changed = false;
    const fix = (rows: LineItem[]) =>
      rows.map((r) => {
        if (r.code !== boundCode) return r;
        let next = r;
        // Migrate calculator-written overrides → quotaY* slots.
        if (r.overrideY1 != null && r.quotaY1 == null) {
          next = { ...next, quotaY1: r.overrideY1, overrideY1: null };
          changed = true;
        }
        if (r.overrideY2 != null && r.quotaY2 == null) {
          next = { ...next, quotaY2: r.overrideY2, overrideY2: null };
          changed = true;
        }
        if (r.overrideY3 != null && r.quotaY3 == null) {
          next = { ...next, quotaY3: r.overrideY3, overrideY3: null };
          changed = true;
        }
        // If neither override nor quota is present on Y1, seed quota
        // to 0 so the locked cell shows a stable value until a quota
        // policy is bound on the Quota view.
        if (next.overrideY1 == null && next.quotaY1 == null) {
          next = { ...next, quotaY1: 0 };
          changed = true;
        }
        return next;
      });
    const income = fix(data.income);
    const expenses = fix(data.expenses);
    return changed ? { ...data, income, expenses } : data;
  };

  // Recovery: rows whose categoryId points to a category that doesn't exist
  // get routed to the kind-appropriate Unsorted bucket. Cross-kind
  // assignments (income item in expense section) are now intentional and
  // preserved. Idempotent — once data is clean this is a no-op.
  const reconcileOrphans = (data: BudgetData): BudgetData => {
    const catById = new Map(data.categories.map((c) => [c.id, c]));
    const categories: Category[] = [...data.categories];
    let changed = false;

    const ensureCat = (id: string, type: 'income' | 'expense', name: string) => {
      if (!catById.has(id)) {
        const c: Category = { id, type, name, displayOrder: 9999 };
        categories.push(c);
        catById.set(id, c);
        changed = true;
      }
    };

    const fix = (rows: LineItem[], fallbackKind: 'income' | 'expense'): LineItem[] =>
      rows.map((r) => {
        if (catById.has(r.categoryId)) return r; // category exists — type mismatch is fine
        const newId = fallbackKind === 'income' ? 'ic_unsorted' : 'ec_unsorted';
        const newName = fallbackKind === 'income' ? 'Unsorted Income' : 'Unsorted Expenses';
        ensureCat(newId, fallbackKind, newName);
        changed = true;
        return { ...r, categoryId: newId };
      });

    const income = fix(data.income, 'income');
    const expenses = fix(data.expenses, 'expense');
    return changed ? { ...data, income, expenses, categories } : data;
  };

  // The backend has historically written the global CPI default into every
  // row's cpiY* column on save. Normalise those values back to null so the
  // "has manual edits" dot only fires for values the user deliberately set.
  const normalizeCpi = (data: BudgetData): BudgetData => {
    const { cpiY1Default, cpiY2Default, cpiY3Default } = data.settings;
    const fix = (r: LineItem): LineItem => ({
      ...r,
      cpiY1: r.cpiY1 == null || r.cpiY1 === cpiY1Default ? null : r.cpiY1,
      cpiY2: r.cpiY2 == null || r.cpiY2 === cpiY2Default ? null : r.cpiY2,
      cpiY3: r.cpiY3 == null || r.cpiY3 === cpiY3Default ? null : r.cpiY3,
    });
    return { ...data, income: data.income.map(fix), expenses: data.expenses.map(fix) };
  };

  const apply = useCallback((result: LoadResult) => {
    const fixed = reconcileBoundForecast(normalizeCpi(reconcileOrphans(result.data)));
    setPristine(fixed);
    setWorking(structuredClone(fixed));
    setHistory([]);
    setSource(result.source);
    setShowHiddenIn(new Set());
    setShowArchivedIn(new Set());
    // Persist the migration if anything changed, fire-and-forget — the
    // user shouldn't have to click Save just to see correctly-routed
    // rows. Gated by inFlightSave to avoid colliding with autosaves.
    if (fixed !== result.data && !inFlightSave.current) {
      inFlightSave.current = true;
      api
        .budgetSaveData(fixed)
        .catch(() => {})
        .finally(() => {
          inFlightSave.current = false;
        });
    }
  }, []);

  // Debounced autosave for quota-driven updates — fires 800ms after the
  // last change so binding tweaks and refreshed run totals land on the
  // Sheet without a manual Save click. Saves are serialised through
  // `inFlightSave` so a long-running save can't collide with a manual
  // Save click or another debounced cycle (which produces the
  // "Another save is in progress" lock error from Apps Script).
  const scheduleQuotaSave = useCallback(
    (nextState: BudgetData) => {
      if (!isRegistrar) return;
      if (quotaSaveTimer.current !== null) {
        window.clearTimeout(quotaSaveTimer.current);
      }
      const fire = () => {
        quotaSaveTimer.current = null;
        if (inFlightSave.current) {
          // Try again after the current save finishes; back off briefly
          // to avoid hammering the lock.
          quotaSaveTimer.current = window.setTimeout(fire, 400);
          return;
        }
        inFlightSave.current = true;
        api
          .budgetSaveData(nextState)
          .then(() => {
            // Use nextState (what we sent) as the new pristine baseline rather
            // than the server's re-read response. The server round-trips through
            // the Sheet which rebuilds objects in column order, so property order
            // differs from our JS objects — JSON.stringify dirty-check would
            // stay true forever even though the data is identical.
            setPristine(structuredClone(nextState));
            setSource('saved');
          })
          .catch((e: Error) => setError(e.message))
          .finally(() => {
            inFlightSave.current = false;
          });
      };
      quotaSaveTimer.current = window.setTimeout(fire, 800);
    },
    [isRegistrar],
  );
  useEffect(() => {
    return () => {
      if (quotaSaveTimer.current !== null) window.clearTimeout(quotaSaveTimer.current);
    };
  }, []);

  // Persist a quota binding and push its FY totals into row 210's quotaY*
  // slots. Deliberately bypasses `mutate` (no history push — same convention
  // as settings changes) and no-ops when the binding and totals already
  // match, so the panel's re-bind-on-entry and the load-time refresh below
  // don't dirty the budget or hammer the Sheet with identical saves.
  const applyQuotaBinding = useCallback(
    (binding: QuotaBinding, fy: { y1: number; y2: number; y3: number }) => {
      setWorking((current) => {
        if (!current) return current;
        const row = current.income.find((i) => !i.deletedAt && i.code === QUOTA_GL_CODE);
        const b = current.settings.quotaBinding;
        const same =
          b?.policyVersion === binding.policyVersion &&
          b?.assessmentYear === binding.assessmentYear &&
          row?.quotaY1 === fy.y1 &&
          row?.quotaY2 === fy.y2 &&
          row?.quotaY3 === fy.y3;
        if (same) return current;
        const next: BudgetData = {
          ...current,
          settings: { ...current.settings, quotaBinding: binding },
          income: current.income.map((i) =>
            i.code === QUOTA_GL_CODE ? { ...i, quotaY1: fy.y1, quotaY2: fy.y2, quotaY3: fy.y3 } : i,
          ),
        };
        scheduleQuotaSave(next);
        return next;
      });
    },
    [scheduleQuotaSave],
  );

  // Autopopulate: when a binding exists, re-run it once per page load so GL
  // 210 reflects the latest curated data + policy without anyone visiting
  // the Quota view. Fire-and-forget — a failed run just leaves the
  // last-saved forecast in place.
  const autoQuotaRan = useRef(false);
  useEffect(() => {
    if (autoQuotaRan.current || !working) return;
    autoQuotaRan.current = true;
    const b = working.settings.quotaBinding;
    if (!b) return;
    const row = working.income.find((i) => !i.deletedAt && i.code === QUOTA_GL_CODE);
    const current210 = row?.currentAmount ?? 0;
    const cpi = working.settings.cpiY1Default;
    api
      .quotaRun(b.policyVersion, b.assessmentYear)
      .then((r) => applyQuotaBinding(b, computeDiocesanFyTotals(r.total_payable, current210, cpi)))
      .catch(() => {});
  }, [working, applyQuotaBinding]);

  // The hub allowlist already authenticated the visitor before serving
  // this page, so we go straight to fetching budget + Xero data.
  useEffect(() => {
    api
      .budgetGetData()
      .then((r) => {
        apply(r);
        setBusy(null);
      })
      .catch((e: Error) => {
        setError(e.message);
        setBusy(null);
      });
    refreshActuals();
    refreshXeroAccounts();
  }, [apply, refreshActuals, refreshXeroAccounts]);

  // Login-audit heartbeat — drives "time active" in the registrar's sign-in
  // trail. Ping /me on mount, every 3 min while the tab is visible, and again
  // whenever it returns to the foreground. Best-effort; touchLoginSession is
  // throttled server-side, so this is cheap. (Without this poll, "time active"
  // would collapse to the login instant.)
  useEffect(() => {
    const beat = () => {
      if (document.visibilityState === 'visible') void api.authMe().catch(() => {});
    };
    beat();
    const id = window.setInterval(beat, 180_000);
    document.addEventListener('visibilitychange', beat);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', beat);
    };
  }, []);

  // Auto-refresh when the tab becomes visible and there are no unsaved
  // edits — keeps data current when switching between machines without
  // requiring a manual page reload.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (busy !== null || history.length > 0 || !working) return;
      setBusy('refresh');
      api
        .budgetGetData()
        .then(apply)
        .catch(() => {})
        .finally(() => setBusy(null));
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [busy, history.length, working, apply]);

  // ── History / undo ──────────────────────────────────────────────────
  // Mutators that change line items push the prior `working` onto the
  // history stack, so the user can step back through edits and (most
  // importantly) row moves. Settings changes (CPI sliders, base year,
  // global show-hidden) intentionally don't push — they're transient
  // tweaks and would otherwise flood the stack. Save and Reset clear the
  // stack as checkpoint events.

  const mutate = useCallback((fn: (w: BudgetData) => BudgetData) => {
    setWorking((current) => {
      if (!current) return current;
      const next = fn(current);
      setHistory((h) => {
        const trimmed = h.length >= HISTORY_CAP ? h.slice(-(HISTORY_CAP - 1)) : h;
        return [...trimmed, current];
      });
      return next;
    });
  }, []);

  const undo = useCallback(() => {
    setHistory((h) => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1];
      setWorking(prev ?? null);
      return h.slice(0, -1);
    });
  }, []);

  // Cmd/Ctrl+Z anywhere except inside an editable input, so the browser's
  // native text-input undo still works while typing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const z = e.key === 'z' || e.key === 'Z';
      if (!z) return;
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.shiftKey) return; // leave Cmd-Shift-Z (redo) alone for future use
      const target = document.activeElement as HTMLElement | null;
      const tag = target?.tagName;
      const isEditable =
        tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable;
      if (isEditable) return;
      e.preventDefault();
      undo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo]);

  // ── Derived ────────────────────────────────────────────────────────
  const dirty = useMemo(
    () => !!pristine && !!working && JSON.stringify(pristine) !== JSON.stringify(working),
    [pristine, working],
  );

  const derived = useMemo(() => (working ? deriveBudget(working) : null), [working]);

  // Set of row IDs that exist in the saved Sheet. Rows added in this
  // session aren't here yet — those get hard-delete instead of archive.
  const pristineIds = useMemo(() => {
    const s = new Set<string>();
    if (!pristine) return s;
    pristine.income.forEach((i) => s.add(i.id));
    pristine.expenses.forEach((e) => s.add(e.id));
    return s;
  }, [pristine]);

  // ── Settings (no history push — see note above) ────────────────────
  const updateSettings = (patch: Partial<Settings>) => {
    setWorking((w) => (w ? { ...w, settings: { ...w.settings, ...patch } } : w));
  };

  const toggleCategoryHidden = (categoryId: string) => {
    setShowHiddenIn((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  };

  const toggleSectionArchived = (sectionId: string) => {
    setShowArchivedIn((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  // ── Line-item mutators (each one calls mutate → pushes history) ────
  const updateLine = (kind: Kind) => (id: string, patch: Partial<LineItem>) => {
    mutate((w) => {
      const key = arrayKey(kind);
      return {
        ...w,
        [key]: w[key].map((it) => (it.id === id ? { ...it, ...patch } : it)),
      };
    });
  };

  // Soft delete — sets a tombstone instead of splicing the row. Recovery
  // happens via the Archive panel (Restore) or undo (⌘Z within session).
  // Once-deleted rows survive saves, so we can recognise "user excluded
  // this" when the future Xero adapter goes to re-sync.
  const deleteLine = (kind: Kind) => (id: string) => {
    mutate((w) => {
      const key = arrayKey(kind);
      const stamp = new Date().toISOString();
      return {
        ...w,
        [key]: w[key].map((it) => (it.id === id ? { ...it, deletedAt: stamp } : it)),
      };
    });
  };

  const restoreLine = (kind: Kind, id: string) => {
    mutate((w) => {
      const key = arrayKey(kind);
      return {
        ...w,
        [key]: w[key].map((it) => (it.id === id ? { ...it, deletedAt: null } : it)),
      };
    });
  };

  // Permanent removal — final on save. Undoable in-session via ⌘Z because
  // mutate() pushes the prior state, but once saved the row is truly gone.
  const purgeLine = (kind: Kind, id: string) => {
    if (
      !window.confirm('Permanently delete this row? This cannot be undone after the next save.')
    ) {
      return;
    }
    mutate((w) => {
      const key = arrayKey(kind);
      return {
        ...w,
        [key]: w[key].filter((it) => it.id !== id),
        settings: { ...w.settings, hidden: w.settings.hidden.filter((x) => x !== id) },
      };
    });
  };

  const toggleHidden = (id: string) => {
    mutate((w) => {
      const isHidden = w.settings.hidden.includes(id);
      return {
        ...w,
        settings: {
          ...w.settings,
          hidden: isHidden ? w.settings.hidden.filter((x) => x !== id) : [...w.settings.hidden, id],
        },
      };
    });
  };

  // Adds a new line item. The `seed` is required so every new row
  // carries a Xero-verified GL code+name — no free-typed placeholders
  // that could drift from the Chart of Accounts.
  const addLine =
    (kind: Kind) =>
    (
      categoryId: string,
      projectId: string | null = null,
      seed?: { code: string; name: string },
    ) => {
      mutate((w) => {
        const key = arrayKey(kind);
        const items = w[key];
        const matches = (it: LineItem): boolean =>
          projectId ? it.projectId === projectId : !it.projectId && it.categoryId === categoryId;
        const lastIdx = items.reduce((acc, it, i) => (matches(it) ? i : acc), -1);
        const baseOrder =
          lastIdx >= 0 ? (items[lastIdx]?.displayOrder ?? 0) + 1 : items.length * 10;
        const next: LineItem = {
          id: newId(kind === 'income' ? 'i' : 'e'),
          categoryId,
          projectId,
          name: seed?.name ?? 'New line item',
          code: seed?.code ?? '',
          currentAmount: 0,
          cpiY1: null,
          cpiY2: null,
          cpiY3: null,
          overrideY1: null,
          overrideY2: null,
          overrideY3: null,
          note: '',
          displayOrder: baseOrder,
          deletedAt: null,
        };
        const updated = [...items];
        const insertAt = lastIdx >= 0 ? lastIdx + 1 : items.length;
        updated.splice(insertAt, 0, next);
        return { ...w, [key]: updated };
      });
    };

  // Hard delete — used for rows that were just added in this session
  // (not yet on the Sheet). No confirm: the row is fresh and the user
  // can re-add via the picker if they change their mind.
  const removeLine = (kind: Kind) => (id: string) => {
    mutate((w) => {
      const key = arrayKey(kind);
      return {
        ...w,
        [key]: w[key].filter((it) => it.id !== id),
        settings: { ...w.settings, hidden: w.settings.hidden.filter((x) => x !== id) },
      };
    });
  };

  const assignProject = (kind: Kind) => (id: string, projectId: string | null) => {
    updateLine(kind)(id, { projectId });
  };

  // ── Drag-and-drop ───────────────────────────────────────────────────
  // Re-orders rows within their kind's array and adjusts categoryId /
  // projectId based on the drop target. Cross-kind drops are blocked
  // by the dragSystem itself.
  const handleDragMove = useCallback(
    (kind: DragKind, draggedId: string, target: MoveTarget) => {
      mutate((w) => {
        const key = arrayKey(kind);
        const items = [...w[key]];
        const draggedIdx = items.findIndex((i) => i.id === draggedId);
        if (draggedIdx < 0) return w;
        const dragged = items[draggedIdx];
        if (!dragged) return w;
        items.splice(draggedIdx, 1);

        if (target.type === 'into-section') {
          // Drop on an operating category header: clear projectId,
          // set categoryId, append to that category's existing items.
          let lastIdx = -1;
          items.forEach((it, i) => {
            if (it.categoryId === target.categoryId && !it.projectId) lastIdx = i;
          });
          const updated = { ...dragged, categoryId: target.categoryId, projectId: null };
          if (lastIdx < 0) items.push(updated);
          else items.splice(lastIdx + 1, 0, updated);
        } else if (target.type === 'into-project') {
          // Drop into a project block: set projectId, append to that
          // project's existing items.
          let lastIdx = -1;
          items.forEach((it, i) => {
            if (it.projectId === target.projectId) lastIdx = i;
          });
          const updated = { ...dragged, projectId: target.projectId };
          if (lastIdx < 0) items.push(updated);
          else items.splice(lastIdx + 1, 0, updated);
        } else {
          // Drop before/after another row: take that row's category
          // and project, insert at appropriate position.
          if (target.id === draggedId) {
            items.splice(draggedIdx, 0, dragged);
            return w;
          }
          const targetIdx = items.findIndex((i) => i.id === target.id);
          if (targetIdx < 0) {
            items.splice(draggedIdx, 0, dragged);
            return w;
          }
          const targetItem = items[targetIdx];
          if (!targetItem) {
            items.splice(draggedIdx, 0, dragged);
            return w;
          }
          const updated = {
            ...dragged,
            categoryId: targetItem.categoryId,
            projectId: targetItem.projectId,
          };
          const insertIdx = target.type === 'before' ? targetIdx : targetIdx + 1;
          items.splice(insertIdx, 0, updated);
        }

        return { ...w, [key]: items };
      });
    },
    [mutate],
  );

  const dragSystem = useDragSystem(handleDragMove);

  // ── Project mutators ────────────────────────────────────────────────
  const addProject = () => {
    mutate((w) => {
      const lastOrder = w.projects.reduce((m, p) => Math.max(m, p.displayOrder), -10);
      return {
        ...w,
        projects: [
          ...w.projects,
          { id: newId('p'), name: 'New Project', displayOrder: lastOrder + 10 },
        ],
      };
    });
  };

  const renameProject = (id: string, name: string) => {
    mutate((w) => ({
      ...w,
      projects: w.projects.map((p) => (p.id === id ? { ...p, name } : p)),
    }));
  };

  const moveProject = (id: string, direction: 1 | -1) => {
    mutate((w) => {
      const sorted = [...w.projects].sort((a, b) => a.displayOrder - b.displayOrder);
      const idx = sorted.findIndex((p) => p.id === id);
      if (idx < 0) return w;
      const targetIdx = idx + direction;
      if (targetIdx < 0 || targetIdx >= sorted.length) return w;
      const a = sorted[idx];
      const b = sorted[targetIdx];
      if (!a || !b) return w;
      return {
        ...w,
        projects: w.projects.map((p) => {
          if (p.id === a.id) return { ...p, displayOrder: b.displayOrder };
          if (p.id === b.id) return { ...p, displayOrder: a.displayOrder };
          return p;
        }),
      };
    });
  };

  // Deletes the project itself; any line items still assigned are
  // returned to operating (their projectId cleared). Confirms first.
  const deleteProject = (id: string) => {
    if (!working) return;
    const assigned =
      working.income.filter((i) => i.projectId === id && !i.deletedAt).length +
      working.expenses.filter((e) => e.projectId === id && !e.deletedAt).length;
    const msg =
      assigned > 0
        ? `This project has ${assigned} line item${assigned > 1 ? 's' : ''} assigned. Deleting will return them to operating. Continue?`
        : 'Delete this project?';
    if (!window.confirm(msg)) return;
    mutate((w) => ({
      ...w,
      projects: w.projects.filter((p) => p.id !== id),
      income: w.income.map((i) => (i.projectId === id ? { ...i, projectId: null } : i)),
      expenses: w.expenses.map((e) => (e.projectId === id ? { ...e, projectId: null } : e)),
    }));
  };

  const addRowToProject = (kind: Kind, projectId: string) => {
    if (!working) return;
    // Pick an arbitrary category of the right type — first one available.
    // (Project-block rows still need a categoryId for schema integrity,
    // but the category never displays inside a project block.)
    const cat = working.categories.find(
      (c) => c.type === (kind === 'income' ? 'income' : 'expense'),
    );
    if (!cat) return;
    addLine(kind)(cat.id, projectId);
  };

  // ── Backend ops ────────────────────────────────────────────────────
  const flashSaved = (msg: string) => {
    setSavedNote(msg);
    window.setTimeout(() => setSavedNote(''), 2500);
  };

  const handleSave = async () => {
    if (!working) return;
    // Viewer mode is read-only — silently no-op rather than throwing
    // a "registrar only" error from the backend. Save button is also
    // hidden, so this is belt-and-braces.
    if (!isRegistrar) return;
    if (inFlightSave.current) return; // serialise with the debounced quota save
    inFlightSave.current = true;
    setBusy('save');
    setError(null);
    try {
      const r = await api.budgetSaveData(working);
      apply(r);
      flashSaved('Saved');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
      inFlightSave.current = false;
    }
  };

  const handleRefresh = async () => {
    setBusy('refresh');
    setError(null);
    try {
      const r = await api.budgetGetData();
      apply(r);
      flashSaved('Refreshed from Sheet');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  // Map GL code → actual amount for the prior fiscal year (baseYear - 1).
  // Empty when no P&L has been imported or no period matches; the table
  // shows '—' for any code that's missing from the lookup.
  const priorPeriod = working ? String(working.settings.baseYear - 1) : '';
  const actualByCode = useMemo(() => {
    const m = new Map<string, number>();
    if (!priorPeriod) return m;
    for (const code of Object.keys(plActuals.byCode)) {
      const v = plActuals.byCode[code]?.[priorPeriod];
      if (typeof v === 'number') m.set(code, Math.round(v));
    }
    return m;
  }, [plActuals, priorPeriod]);

  // Wipe-and-replace. Xero is the trust sheet for GL lines, so every
  // sync nukes the budget structure and rebuilds it from Xero's revenue
  // + expense accounts. New rows arrive at currentAmount=0 in an
  // Unsorted category; the subsequent xlsx upload supplies values and
  // section grouping.
  type SyncSummary = {
    accountsCached: number;
    income: number;
    expenses: number;
    gstProfilesUpdated?: number;
  };

  const syncBudgetFromXero = useCallback(async (): Promise<SyncSummary | null> => {
    if (!working) return null;
    // Single round-trip: server pulls live from Xero's API and returns
    // the filtered budget-ready list inline. We never re-read the
    // Xero Accounts sheet tab here — that's just for human visibility.
    const accountsResult = await api.budgetSyncGL();
    const xeroAccounts = accountsResult.forBudget;
    if (xeroAccounts.length === 0) {
      return { accountsCached: accountsResult.count, income: 0, expenses: 0 };
    }

    const newIncome: LineItem[] = [];
    const newExpenses: LineItem[] = [];
    let order = 0;
    for (const a of xeroAccounts) {
      order += 10;
      const item: LineItem = {
        id: `${a.kind === 'income' ? 'i' : 'e'}_xero_${a.code}`,
        categoryId: a.kind === 'income' ? 'ic_unsorted' : 'ec_unsorted',
        projectId: null,
        name: a.name,
        code: a.code,
        currentAmount: 0,
        cpiY1: null,
        cpiY2: null,
        cpiY3: null,
        overrideY1: null,
        overrideY2: null,
        overrideY3: null,
        note: '',
        displayOrder: order,
        deletedAt: null,
      };
      if (a.kind === 'income') newIncome.push(item);
      else newExpenses.push(item);
    }

    const categories: Category[] = [
      { id: 'ic_unsorted', type: 'income', name: 'Unsorted Income', displayOrder: 1 },
      { id: 'ec_unsorted', type: 'expense', name: 'Unsorted Expenses', displayOrder: 1 },
    ];

    // Build the next state explicitly (rather than via mutate→setState
    // which is async) so we can persist it to the Sheet immediately.
    // Sync is destructive; auto-saving prevents the user from getting
    // out-of-sync between working state and the saved Sheet.
    const next: BudgetData = {
      ...working,
      income: newIncome,
      expenses: newExpenses,
      categories,
      projects: [],
      settings: { ...working.settings, hidden: [] },
    };
    while (inFlightSave.current) {
      await new Promise((r) => setTimeout(r, 200));
    }
    inFlightSave.current = true;
    try {
      const saved = await api.budgetSaveData(next);
      apply(saved);
    } finally {
      inFlightSave.current = false;
    }

    return {
      accountsCached: accountsResult.count,
      income: newIncome.length,
      expenses: newExpenses.length,
      gstProfilesUpdated: accountsResult.gstProfilesUpdated ?? 0,
    };
  }, [working, apply]);

  // Slice D — explicit escape hatch. Overwrites the tool-managed
  // sections with the structure from the persisted Xero Performance
  // tab. Restores the kind-aware logic that used to fire on every
  // xlsx import. Each Tab-2 section gets BOTH ic_ and ec_ category
  // pairs; rows are reassigned to the matching one based on their
  // own kind.
  const reapplySectionsFromXlsx = useCallback(async (): Promise<{
    sectionsApplied: number;
    rowsReassigned: number;
  } | null> => {
    if (!working) return null;
    const perf = await api.budgetPerformance();
    if (perf.sectionsInOrder.length === 0) {
      return { sectionsApplied: 0, rowsReassigned: 0 };
    }

    const slug = (s: string) =>
      s
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '') || 'section';

    let categories: Category[] = [...working.categories];
    const sectionMap: Record<string, { incomeId: string; expenseId: string }> = {};
    perf.sectionsInOrder.forEach((s, idx) => {
      const sl = slug(s.name);
      const incomeId = 'ic_' + sl;
      const expenseId = 'ec_' + sl;
      sectionMap[s.name] = { incomeId, expenseId };
      const order = (idx + 1) * 10;
      const upsert = (id: string, type: 'income' | 'expense') => {
        const existingIdx = categories.findIndex((c) => c.id === id);
        if (existingIdx === -1) {
          categories = [...categories, { id, type, name: s.name, displayOrder: order }];
        } else {
          categories = categories.map((c) =>
            c.id === id ? { ...c, displayOrder: order, name: s.name } : c,
          );
        }
      };
      upsert(incomeId, 'income');
      upsert(expenseId, 'expense');
    });

    const codeOrder: Record<string, number> = {};
    Object.keys(perf.byCode).forEach((code, i) => {
      codeOrder[code] = (i + 1) * 10;
    });

    let rowsReassigned = 0;
    const transform = (rows: LineItem[], kind: 'income' | 'expense'): LineItem[] =>
      rows.map((r) => {
        const next = { ...r };
        const section = perf.sectionByCode[r.code];
        const map = section ? sectionMap[section] : null;
        const newCatId = map ? (kind === 'income' ? map.incomeId : map.expenseId) : null;
        if (newCatId && r.categoryId !== newCatId) {
          next.categoryId = newCatId;
          rowsReassigned += 1;
        }
        const newOrder = codeOrder[r.code];
        if (typeof newOrder === 'number') next.displayOrder = newOrder;
        return next;
      });

    const nextState: BudgetData = {
      ...working,
      income: transform(working.income, 'income'),
      expenses: transform(working.expenses, 'expense'),
      categories,
    };

    while (inFlightSave.current) {
      await new Promise((r) => setTimeout(r, 200));
    }
    inFlightSave.current = true;
    try {
      const saved = await api.budgetSaveData(nextState);
      apply(saved);
    } finally {
      inFlightSave.current = false;
    }

    return {
      sectionsApplied: perf.sectionsInOrder.length,
      rowsReassigned,
    };
  }, [working, apply]);

  // After an xlsx upload: amounts only. Tab-2 section grouping/ordering
  // is no longer applied — sections are tool-managed (the canonical
  // structure lives on the data Sheet). The xlsx supplies values for
  // matching GL codes; codes not in Tab 2 are zeroed.
  const applyXlsxToBudget = useCallback(async (): Promise<{
    amountsMatched: number;
    amountsZeroed: number;
    periodUsed: string;
  } | null> => {
    if (!working) return null;
    const perf = await api.budgetPerformance();
    if (perf.periods.length === 0) {
      // No Performance tab in the upload (e.g. P&L-only file).
      return null;
    }

    const baseYearStr = String(working.settings.baseYear);
    const period = perf.periods.includes(baseYearStr)
      ? baseYearStr
      : [...perf.periods].sort().at(-1) || '';
    if (!period) return { amountsMatched: 0, amountsZeroed: 0, periodUsed: '' };

    let amountsMatched = 0;
    let amountsZeroed = 0;

    const transform = (rows: LineItem[]): LineItem[] =>
      rows.map((r) => {
        if (!r.code) return r;
        const v = perf.byCode[r.code]?.[period];
        if (typeof v === 'number') {
          amountsMatched += 1;
          return { ...r, currentAmount: Math.round(v) };
        }
        if (r.currentAmount !== 0) amountsZeroed += 1;
        return { ...r, currentAmount: 0 };
      });

    const nextState: BudgetData = {
      ...working,
      income: transform(working.income),
      expenses: transform(working.expenses),
    };

    while (inFlightSave.current) {
      await new Promise((r) => setTimeout(r, 200));
    }
    inFlightSave.current = true;
    let saved;
    try {
      saved = await api.budgetSaveData(nextState);
      apply(saved);
    } finally {
      inFlightSave.current = false;
    }

    return {
      amountsMatched,
      amountsZeroed,
      periodUsed: period,
    };
  }, [working, apply]);

  const handlePrint = () => window.print();
  const handleCsv = () => {
    if (!working || !derived) return;
    downloadBudgetCsv(derived, working.settings.baseYear);
  };
  const handleXlsx = async () => {
    if (!working || !derived) return;
    try {
      await downloadBudgetXlsx(derived, working.settings.baseYear);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <ViewerContext.Provider value={isViewer}>
      <div className="min-h-screen bg-paper-cream text-ccaanz-black print:bg-white">
        {working && derived && (
          <Masthead
            baseYear={working.settings.baseYear}
            onBaseYearChange={(y) => updateSettings({ baseYear: y })}
            preparedDate={new Date()}
            source={source}
            dirty={dirty}
            busy={busy === 'save' || busy === 'refresh'}
            savedNote={savedNote}
          />
        )}

        <main className="max-w-[1100px] mx-auto px-7 py-7">
          {error && (
            <div className="panel mb-4 no-print">
              <div className="panel-section">
                <div className="text-[15px] font-medium text-ccaanz-red mb-1">Backend error</div>
                <div className="text-ccaanz-grey num text-[12px]">{error}</div>
              </div>
            </div>
          )}

          {!working && !error && (
            <div className="panel">
              <div className="panel-section py-16 text-center text-[13px] text-ccaanz-grey">
                Loading…
              </div>
            </div>
          )}

          {working && derived && view === 'sections' && (
            <SectionsPage
              onBack={() => setView('budget')}
              categories={working.categories}
              projects={working.projects}
              income={working.income}
              expenses={working.expenses}
              onApply={(patch) => {
                if (!working) return;
                const next: BudgetData = {
                  ...working,
                  categories: patch.categories,
                  projects: patch.projects,
                  income: patch.income,
                  expenses: patch.expenses,
                };
                mutate(() => next);
                scheduleQuotaSave(next);
              }}
            />
          )}

          {working && derived && view === 'cashflow' && (
            <CashflowPage
              budget={working}
              onBack={() => setView('budget')}
              isViewer={isViewer}
              isRegistrar={isRegistrar}
              onApplyToBudget={async (updated) => {
                const result = await api.budgetSaveData(updated);
                apply(result);
                setView('budget');
              }}
            />
          )}

          {working && derived && view === 'budget' && (
            <>
              <ControlPanel
                settings={working.settings}
                baseYear={working.settings.baseYear}
                onSettingsChange={updateSettings}
                onSave={handleSave}
                onRefresh={handleRefresh}
                onPrint={handlePrint}
                onCsv={handleCsv}
                onXlsx={handleXlsx}
                onUndo={undo}
                canUndo={history.length > 0}
                undoDepth={history.length}
                dirty={dirty}
                busy={busy}
                hiddenCount={working.settings.hidden.length}
                {...(isRegistrar ? { onAdmin: () => setAdminOpen(true) } : {})}
                onQuota={() => setQuotaOpen(true)}
                onSections={() => setView('sections')}
                onCashflow={() => setView('cashflow')}
                isViewer={isViewer ?? false}
              />

              <SummaryStrip
                derived={derived}
                baseYear={working.settings.baseYear}
                cpiY1={working.settings.cpiY1Default}
                cpiY2={working.settings.cpiY2Default}
                cpiY3={working.settings.cpiY3Default}
              />

              <BudgetTable
                title="Operating Income"
                subtitle="Grants, Donations, Parish Assessments, Reserves"
                kind="income"
                groups={derived.incomeByCategory}
                totals={derived.operatingIncomeTotals}
                defaults={derived.defaults}
                baseYear={working.settings.baseYear}
                projects={working.projects}
                hidden={working.settings.hidden}
                showHidden={working.settings.showHidden}
                showHiddenIn={showHiddenIn}
                showArchivedIn={showArchivedIn}
                dragSystem={dragSystem}
                priorPeriod={priorPeriod}
                actualByCode={actualByCode}
                quotaBoundLineCode={QUOTA_GL_CODE}
                onToggleCategoryHidden={toggleCategoryHidden}
                onToggleCategoryArchived={toggleSectionArchived}
                onLineUpdate={(k, id, patch) => updateLine(k)(id, patch)}
                onLineDelete={(k, id) => deleteLine(k)(id)}
                onLineRemove={(k, id) => removeLine(k)(id)}
                onLineToggleHidden={toggleHidden}
                onLineAdd={(catId, seed) => addLine('income')(catId, null, seed)}
                onLineAssignProject={(k, id, pid) => assignProject(k)(id, pid)}
                onLineRestore={(k, id) => restoreLine(k, id)}
                xeroAccounts={xeroAccounts}
                pristineIds={pristineIds}
              />

              <BudgetTable
                title="Operating Expenses"
                subtitle="Grouped by diocesan cost centre"
                kind="expense"
                priorPeriod={priorPeriod}
                actualByCode={actualByCode}
                netActualOpposite={derived.incomeByCategory.reduce(
                  (s, g) =>
                    s +
                    sumActuals(g.incomeItems, actualByCode) -
                    sumActuals(g.expenseItems, actualByCode),
                  0,
                )}
                groups={derived.expensesByCategory}
                totals={derived.operatingExpenseTotals}
                defaults={derived.defaults}
                baseYear={working.settings.baseYear}
                projects={working.projects}
                hidden={working.settings.hidden}
                showHidden={working.settings.showHidden}
                showHiddenIn={showHiddenIn}
                showArchivedIn={showArchivedIn}
                dragSystem={dragSystem}
                onToggleCategoryHidden={toggleCategoryHidden}
                onToggleCategoryArchived={toggleSectionArchived}
                netRow={{ label: 'Net Operating', values: derived.operatingNet }}
                startingLineNo={derived.incomeByCategory.reduce(
                  (acc, g) =>
                    acc +
                    [...g.incomeItems, ...g.expenseItems].filter(
                      (i) =>
                        working.settings.showHidden ||
                        showHiddenIn.has(g.category.id) ||
                        !working.settings.hidden.includes(i.id),
                    ).length,
                  1,
                )}
                onLineUpdate={(k, id, patch) => updateLine(k)(id, patch)}
                onLineDelete={(k, id) => deleteLine(k)(id)}
                onLineRemove={(k, id) => removeLine(k)(id)}
                onLineToggleHidden={toggleHidden}
                onLineAdd={(catId, seed) => addLine('expense')(catId, null, seed)}
                onLineAssignProject={(k, id, pid) => assignProject(k)(id, pid)}
                onLineRestore={(k, id) => restoreLine(k, id)}
                xeroAccounts={xeroAccounts}
                pristineIds={pristineIds}
              />

              {derived.financialIncomeByCategory.length > 0 && (
                <BudgetTable
                  title="Non-Operating Income"
                  subtitle="Investment returns, interest, and other non-operating receipts"
                  kind="income"
                  groups={derived.financialIncomeByCategory}
                  totals={derived.financialIncomeTotals}
                  defaults={derived.defaults}
                  baseYear={working.settings.baseYear}
                  projects={working.projects}
                  hidden={working.settings.hidden}
                  showHidden={working.settings.showHidden}
                  showHiddenIn={showHiddenIn}
                  showArchivedIn={showArchivedIn}
                  dragSystem={dragSystem}
                  priorPeriod={priorPeriod}
                  actualByCode={actualByCode}
                  onToggleCategoryHidden={toggleCategoryHidden}
                  onToggleCategoryArchived={toggleSectionArchived}
                  startingLineNo={
                    derived.incomeByCategory.reduce(
                      (acc, g) =>
                        acc +
                        [...g.incomeItems, ...g.expenseItems].filter(
                          (i) =>
                            working.settings.showHidden ||
                            showHiddenIn.has(g.category.id) ||
                            !working.settings.hidden.includes(i.id),
                        ).length,
                      0,
                    ) +
                    derived.expensesByCategory.reduce(
                      (acc, g) =>
                        acc +
                        [...g.incomeItems, ...g.expenseItems].filter(
                          (i) =>
                            working.settings.showHidden ||
                            showHiddenIn.has(g.category.id) ||
                            !working.settings.hidden.includes(i.id),
                        ).length,
                      0,
                    ) +
                    1
                  }
                  onLineUpdate={(k, id, patch) => updateLine(k)(id, patch)}
                  onLineDelete={(k, id) => deleteLine(k)(id)}
                  onLineRemove={(k, id) => removeLine(k)(id)}
                  onLineToggleHidden={toggleHidden}
                  onLineAdd={(catId, seed) => addLine('income')(catId, null, seed)}
                  onLineAssignProject={(k, id, pid) => assignProject(k)(id, pid)}
                  onLineRestore={(k, id) => restoreLine(k, id)}
                  xeroAccounts={xeroAccounts}
                  pristineIds={pristineIds}
                />
              )}

              {derived.financialExpensesByCategory.length > 0 && (
                <BudgetTable
                  title="Non-Operating Expenses"
                  subtitle="Investment costs, bank charges, and other non-operating outgoings"
                  kind="expense"
                  groups={derived.financialExpensesByCategory}
                  totals={derived.financialExpenseTotals}
                  defaults={derived.defaults}
                  baseYear={working.settings.baseYear}
                  projects={working.projects}
                  hidden={working.settings.hidden}
                  showHidden={working.settings.showHidden}
                  showHiddenIn={showHiddenIn}
                  showArchivedIn={showArchivedIn}
                  dragSystem={dragSystem}
                  priorPeriod={priorPeriod}
                  actualByCode={actualByCode}
                  onToggleCategoryHidden={toggleCategoryHidden}
                  onToggleCategoryArchived={toggleSectionArchived}
                  netRow={{ label: 'Net Non-Operating', values: derived.financialNet }}
                  startingLineNo={
                    derived.incomeByCategory.reduce(
                      (acc, g) =>
                        acc +
                        [...g.incomeItems, ...g.expenseItems].filter(
                          (i) =>
                            working.settings.showHidden ||
                            showHiddenIn.has(g.category.id) ||
                            !working.settings.hidden.includes(i.id),
                        ).length,
                      0,
                    ) +
                    derived.expensesByCategory.reduce(
                      (acc, g) =>
                        acc +
                        [...g.incomeItems, ...g.expenseItems].filter(
                          (i) =>
                            working.settings.showHidden ||
                            showHiddenIn.has(g.category.id) ||
                            !working.settings.hidden.includes(i.id),
                        ).length,
                      0,
                    ) +
                    derived.financialIncomeByCategory.reduce(
                      (acc, g) =>
                        acc +
                        [...g.incomeItems, ...g.expenseItems].filter(
                          (i) =>
                            working.settings.showHidden ||
                            showHiddenIn.has(g.category.id) ||
                            !working.settings.hidden.includes(i.id),
                        ).length,
                      0,
                    ) +
                    1
                  }
                  onLineUpdate={(k, id, patch) => updateLine(k)(id, patch)}
                  onLineDelete={(k, id) => deleteLine(k)(id)}
                  onLineRemove={(k, id) => removeLine(k)(id)}
                  onLineToggleHidden={toggleHidden}
                  onLineAdd={(catId, seed) => addLine('expense')(catId, null, seed)}
                  onLineAssignProject={(k, id, pid) => assignProject(k)(id, pid)}
                  onLineRestore={(k, id) => restoreLine(k, id)}
                  xeroAccounts={xeroAccounts}
                  pristineIds={pristineIds}
                />
              )}

              <ProjectsSection
                groups={derived.projects}
                totalNetCurrent={derived.totalProjectsNet.current}
                defaults={derived.defaults}
                baseYear={working.settings.baseYear}
                projects={working.projects}
                hidden={working.settings.hidden}
                showHidden={working.settings.showHidden}
                showArchivedIn={showArchivedIn}
                dragSystem={dragSystem}
                priorPeriod={priorPeriod}
                actualByCode={actualByCode}
                quotaBoundLineCode={QUOTA_GL_CODE}
                onToggleProjectArchived={toggleSectionArchived}
                startingLineNo={countVisibleRows(derived, working.settings, showHiddenIn) + 1}
                onRenameProject={renameProject}
                onMoveProject={moveProject}
                onDeleteProject={deleteProject}
                onAddProject={addProject}
                onAddRow={addRowToProject}
                onLineUpdate={(kind) => updateLine(kind)}
                onLineDelete={(kind) => deleteLine(kind)}
                onLineToggleHidden={toggleHidden}
                onLineAssignProject={(kind) => assignProject(kind)}
                onLineRestore={(kind, id) => restoreLine(kind, id)}
              />

              <GrandTotalPanel
                baseYear={working.settings.baseYear}
                operatingNet={derived.operatingNet}
                totalProjectsNet={derived.totalProjectsNet}
                financialNet={derived.financialNet}
                grandNet={derived.grandNet}
                hasProjects={derived.projects.length > 0}
                hasFinancial={
                  derived.financialIncomeByCategory.length > 0 ||
                  derived.financialExpensesByCategory.length > 0
                }
              />

              <HistoricalStrip />

              <ArchivePanel
                income={working.income}
                expenses={working.expenses}
                onRestore={(kind, id) => restoreLine(kind, id)}
                onPurge={(kind, id) => purgeLine(kind, id)}
              />

              {quotaOpen && (
                <QuotaPolicyPanel
                  onClose={() => setQuotaOpen(false)}
                  baseYear={working.settings.baseYear}
                  cpiY1={working.settings.cpiY1Default}
                  boundLine={(() => {
                    const r = working.income.find((i) => !i.deletedAt && i.code === QUOTA_GL_CODE);
                    return r
                      ? { code: r.code, name: r.name, currentAmount: r.currentAmount }
                      : null;
                  })()}
                  binding={working.settings.quotaBinding ?? null}
                  onBind={applyQuotaBinding}
                  onClear={() => {
                    if (!working) return;
                    const next: BudgetData = {
                      ...working,
                      settings: { ...working.settings, quotaBinding: null },
                    };
                    mutate(() => next);
                    scheduleQuotaSave(next);
                  }}
                />
              )}

              {adminOpen && (
                <AdminPanel
                  onClose={() => setAdminOpen(false)}
                  onActualsChange={refreshActuals}
                  budgetCodes={[
                    ...working.income.filter((i) => !i.deletedAt).map((i) => i.code),
                    ...working.expenses.filter((e) => !e.deletedAt).map((e) => e.code),
                  ]}
                  onSyncBudgetFromXero={syncBudgetFromXero}
                  onApplyXlsxToBudget={applyXlsxToBudget}
                  onReapplySectionsFromXlsx={reapplySectionsFromXlsx}
                />
              )}

              <footer className="mt-10 pt-6 border-t border-edge text-[12px] flex flex-wrap justify-between gap-4 text-ccaanz-grey">
                <div>CCAANZ Diocesan Budget Tool · Three-Year Projection · v6.0</div>
                <div className="text-right">
                  <div className="uppercase tracking-[0.08em] text-[10px] font-medium">
                    Office of the Registrar
                  </div>
                  <div className="text-ccaanz-black font-medium">
                    Steve Jukes — Registrar &amp; Executive Officer
                  </div>
                </div>
              </footer>
            </>
          )}
        </main>
      </div>
    </ViewerContext.Provider>
  );
}
